import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { isEmpty } from "lodash";
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Route, Routes, useNavigate } from "react-router";
import { KEY, SECTION, STEP, USER_DATA } from "./constants/storage";
import { useNavigationHandler } from "./contexts/Navigation";
import { getSessionStorage } from "./helpers/browserStorage";
import usePathChunk from "./hooks/usePathChunk";
import useSegmentTrack from "./hooks/useSegmentTrack";
import ErrorPage from "./pages/ErrorPage";
import Home from "./pages/Home";
import FleetAccountInfo from "./pages/FleetAccountInfo";
import Layout from "./pages/Layout";

import {
  FETCH_SEGMENT_EVENTS,
  FETCH_VEHICLES,
} from "./react-query/QueryConstant";
import { setCommonLocation } from "./redux/slices/CommonLocation";
import {
  getPersonalInfo,
  setPersonalInfo,
} from "./redux/slices/PersonalAccountInformationSlice";
import {
  fetchEventsSuccess,
  getSegmentEvents,
} from "./redux/slices/SegmentEvents";
import { fetchSegmentEvents } from "./services/KlaviyoEvents";
import { createUserSession, isUserSignIn } from "./services/Session";
import { routerPaths, routeValues, STEP_ID } from "./utils/routerPaths";
import EnrollVehicles from "./pages/EnrollVehicles";
import DownloadMembershipCard from "./pages/DownloadMembershipCard";

const AppRoutes = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const queryClient = useQueryClient();

  const { setCurrentStep, setCurrentSection } = useNavigationHandler();
  const { getPathChunk } = usePathChunk();

  const { trackEventWithIndentity } = useSegmentTrack();
  const segmentData = useSelector((state) => getSegmentEvents(state));
  const personalInfo = useSelector((state) => getPersonalInfo(state));

  const { mutateAsync: createSession } = useMutation({
    mutationFn: (sessionData) => {
      return createUserSession({
        apiPayload: sessionData,
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries(FETCH_VEHICLES);
    },
  });

  const { data: events } = useQuery({
    queryKey: [FETCH_SEGMENT_EVENTS],
    queryFn: () => fetchSegmentEvents(),
    enabled: isEmpty(segmentData),
  });

  const trackKlaviyoWelcomeEvents = () => {
    if (personalInfo?.sign_in_count === 0) {
      trackEventWithIndentity({
        traits: {},
        eventName: "sfo-welcome-email-sent",
        props: {},
      });
      trackEventWithIndentity({
        traits: {},
        eventName: "sfo-first-time-login",
        props: {},
      });
    }
  };

  useEffect(() => {
    if (!isEmpty(events)) {
      dispatch(fetchEventsSuccess(events));
    }
  }, [events, dispatch]);

  useEffect(() => {
    if (!isEmpty(segmentData) && !isEmpty(personalInfo)) {
      trackKlaviyoWelcomeEvents();
    }
  }, [segmentData, personalInfo]);

  const handleUserSession = async () => {
    const userSessionData = JSON.parse(getSessionStorage(USER_DATA));
    const urlParams = new URLSearchParams(window.location.search);
    const key = urlParams.get(KEY);
    const isUserSignedIn = isUserSignIn();

    if (key) {
      if (isUserSignedIn && userSessionData?.authentication_token === key) {
        dispatch(setPersonalInfo({ personalInfo: userSessionData }));
        dispatch(
          setCommonLocation({ commonLocation: userSessionData.address })
        );
      } else {
        await createSession({ key });
      }
    } else if (isUserSignedIn) {
      dispatch(setPersonalInfo({ personalInfo: userSessionData }));
      dispatch(setCommonLocation({ commonLocation: userSessionData.address }));
    }
  };

  useEffect(() => {
    handleUserSession();
  }, []);

  return (
    <Routes>
      <Route path={routerPaths.home} element={<Layout />}>
        <Route index path={routerPaths.home} element={<Home />} />
        <Route index path={routerPaths.fleetAccountInfo} element={<FleetAccountInfo />} />
        <Route path={routerPaths.enrollVehicles} element={<EnrollVehicles />} />
        <Route path={routerPaths.downloadMembershipCard} element={<DownloadMembershipCard />} />
      </Route>
      <Route path="*" element={<ErrorPage isRouteMissing />} />
    </Routes>
  );
};

export default AppRoutes;
